import { tx as translateInterfaceText } from '@/utils/i18n';
export type PrimeTimeMetric = 'plays' | 'views';
export type ConversionMetric = 'conversions' | 'conversionRate' | 'revenue';
export type HeatmapMode = 'volume' | 'result';
export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type PrimeTimesMode = 'general' | 'result';

export interface PrimeTimeData {
  weekday: WeekdayIndex;
  hourBucket: number;
  playsUnique: number;
  viewsUnique: number;
  deviceDominant?: string;
  devicePercentage?: number;
  conversions?: number;
  revenue?: number;
  // Taxa de conversão por célula (conversions / playsUnique * 100). Derivada no transform.
  conversionRate?: number;
}

export interface PrimeTimesMatrix {
  bucketHours: number;
  timezone: string;
  matrix: PrimeTimeData[];
  max: {
    playsUnique: number;
    viewsUnique: number;
  };
}

export interface BucketDefinition {
  index: number;
  label: string;
  fromHour: number;
  toHour: number;
}

export interface WeekdayDefinition {
  index: WeekdayIndex;
  label: string;
  fullLabel: string;
}

export const WEEKDAYS: WeekdayDefinition[] = [
  { index: 0, get label() { return translateInterfaceText("SEG"); }, fullLabel: 'Segunda-feira' },
  { index: 1, get label() { return translateInterfaceText("TER"); }, fullLabel: 'Terça-feira' },
  { index: 2, get label() { return translateInterfaceText("QUA"); }, fullLabel: 'Quarta-feira' },
  { index: 3, get label() { return translateInterfaceText("QUI"); }, fullLabel: 'Quinta-feira' },
  { index: 4, get label() { return translateInterfaceText("SEX"); }, fullLabel: 'Sexta-feira' },
  { index: 5, get label() { return translateInterfaceText("SÁB"); }, fullLabel: 'Sábado' },
  { index: 6, get label() { return translateInterfaceText("DOM"); }, fullLabel: 'Domingo' }
];

export const HOUR_BUCKETS: BucketDefinition[] = [
  { index: 0, label: '00h-02h', fromHour: 0, toHour: 2 },
  { index: 1, label: '02h-04h', fromHour: 2, toHour: 4 },
  { index: 2, label: '04h-06h', fromHour: 4, toHour: 6 },
  { index: 3, label: '06h-08h', fromHour: 6, toHour: 8 },
  { index: 4, label: '08h-10h', fromHour: 8, toHour: 10 },
  { index: 5, label: '10h-12h', fromHour: 10, toHour: 12 },
  { index: 6, label: '12h-14h', fromHour: 12, toHour: 14 },
  { index: 7, label: '14h-16h', fromHour: 14, toHour: 16 },
  { index: 8, label: '16h-18h', fromHour: 16, toHour: 18 },
  { index: 9, label: '18h-20h', fromHour: 18, toHour: 20 },
  { index: 10, label: '20h-22h', fromHour: 20, toHour: 22 },
  { index: 11, label: '22h-24h', fromHour: 22, toHour: 24 }
];

export interface CellDetail {
  weekday: WeekdayIndex;
  bucket: BucketDefinition;
  value: number;
  percent: number;
  metric: PrimeTimeMetric;
}

export interface ConversionData {
  conversions: number;
  clicks: number;
  conversionRate: number;
  revenue: number;
  averageTicket: number;
}

export interface PrimeTimeDataExtended extends PrimeTimeData {
  conversions: number;
  clicks: number;
  conversionRate: number;
  revenue: number;
  device?: 'desktop' | 'mobile' | 'tablet';
  sampleSize: number;
  isLowSample: boolean;
}

export interface PrimeTimesMatrixExtended extends Omit<PrimeTimesMatrix, 'matrix'> {
  matrix: PrimeTimeDataExtended[];
  maxConversion: {
    conversions: number;
    conversionRate: number;
    revenue: number;
  };
}

export interface TopWindow {
  weekday: WeekdayIndex;
  bucket: BucketDefinition;
  value: number;
  label: string;
  isLowSample: boolean;
}

export interface ConsistentWindow extends TopWindow {
  stdDeviation: number;
  variation: string;
  occurrences: number;
}

export interface DeviceBestWindow {
  device: 'desktop' | 'mobile' | 'tablet';
  window: TopWindow;
  deviceIcon: string;
}

export interface ConversionPeaksData {
  topWindows: TopWindow[];
  absolutePeak: TopWindow;
  consistentWindow: ConsistentWindow;
  deviceWindows: DeviceBestWindow[];
}
